import cn from "../../lib/classnames";
import prevButton from "../../images/leftButton.svg";
import nextButton from "../../images/rightButton.svg";
import React, {useEffect, useRef, useState} from "react";
import ReviewDisplay from "../ReviewDisplay";
import RoundedCard from "../RoundedCard";
import styles from "./ReviewCarousel.module.css";
import callApplicationLead from "../../lib/callApplicationLead";

export type ReviewDisplayInfo = {
    id: string;
    rating: number;
    description: string;
    dateOfReview: string;
};

const strings = {
    next: "Next Review",
    prev: "Previous Review",
};

type Props = {
    reviews: ReviewDisplayInfo[];
    clearInterval: boolean;
    windowClassName?: string;
    autoScrollTimeMilli?: number;
    inviteCode?: string;
};

function getPrevIndex(curr: number, reviewLength: number): number {
    if (curr - 1 < 0) {
        return reviewLength - 1;
    } else {
        return curr - 1;
    }
}
function getNextIndex(curr: number, reviewLength: number): number {
    if (curr + 1 >= reviewLength) {
        return 0;
    } else {
        return curr + 1;
    }
}

function getClassName(index: number, prevIndex: number, currentIndex: number, nextIndex: number) {
    switch (index) {
        case prevIndex: {
            return styles.prevBlock;
        }
        case currentIndex: {
            return styles.currentBlock;
        }
        case nextIndex: {
            return styles.nextBlock;
        }
        default:
            return styles.notShown;
    }
}

export default function ReviewCarousel(props: Props) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const prevIndex = getPrevIndex(currentIndex, props.reviews.length);
    const nextIndex = getNextIndex(currentIndex, props.reviews.length);
    const intervalID = useRef(0);

    useEffect(() => {
        function autoScroll() {
            setCurrentIndex((prevCurrentIndex) =>
                getNextIndex(prevCurrentIndex, props.reviews.length),
            );
        }
        if (props.autoScrollTimeMilli) {
            intervalID.current = window.setInterval(autoScroll, props.autoScrollTimeMilli);
        }
    }, [props.reviews.length, props.autoScrollTimeMilli, intervalID]);

    useEffect(() => {
        if (props.clearInterval) {
            window.clearInterval(intervalID.current);
        }
    }, [props.clearInterval]);

    function resetInterval() {
        function autoScroll() {
            setCurrentIndex((prevCurrentIndex) =>
                getNextIndex(prevCurrentIndex, props.reviews.length),
            );
        }
        if (props.autoScrollTimeMilli) {
            window.clearInterval(intervalID.current);
            intervalID.current = window.setInterval(autoScroll, props.autoScrollTimeMilli);
        }
    }

    return (
        <>
            <div
                className={cn(
                    styles.window,
                    props.windowClassName ? props.windowClassName : styles.windowDefault,
                )}
            >
                <div className={styles.currentContainer}>
                    <button
                        className={cn(styles.button, styles.prevButton)}
                        onClick={() => {
                            setCurrentIndex(prevIndex);
                            resetInterval();
                            callApplicationLead(
                                "review-carousel-prev",
                                props.inviteCode ? props.inviteCode : null,
                                window.location.href,
                            );
                        }}
                        type="button"
                    >
                        <img className={styles.buttonImg} src={prevButton} alt={strings.prev} />
                    </button>
                    {props.reviews.map((review, reviewIndex) => {
                        const className = getClassName(
                            reviewIndex,
                            prevIndex,
                            currentIndex,
                            nextIndex,
                        );
                        return (
                            <RoundedCard
                                key={review.id}
                                showBorder={false}
                                className={cn(className, styles.block)}
                            >
                                <ReviewDisplay
                                    description={review.description}
                                    dateOfReview={review.dateOfReview}
                                />
                            </RoundedCard>
                        );
                    })}
                    <button
                        className={cn(styles.button, styles.nextButton)}
                        onClick={() => {
                            setCurrentIndex(nextIndex);
                            resetInterval();
                            callApplicationLead(
                                "review-carousel-next",
                                props.inviteCode ? props.inviteCode : null,
                                window.location.href,
                            );
                        }}
                        type="button"
                    >
                        <img className={styles.buttonImg} src={nextButton} alt={strings.next} />
                    </button>
                </div>
            </div>
        </>
    );
}
